import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Building2, MailCheck, UserRound } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchRegistrationCities,
  registerClient,
} from "../api/auth";
import { useAuth } from "../context/AuthContext";
import { STANDARD_RU_CITIES } from "../constants/cities";
import {
  formatRuPhone,
  getPasswordConfirmationError,
  getPasswordError,
  normalizeApiError,
} from "../utils/authValidation";

type FormState = {
  company_name: string;
  inn: string;
  city: string;
  fullname: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
};

const initialForm: FormState = {
  company_name: "",
  inn: "",
  city: "",
  fullname: "",
  email: "",
  phone: "",
  password: "",
  password_confirmation: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-4 focus:ring-indigo-200/60";

export default function RegisterClientPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState<FormState>(initialForm);
  const [cities, setCities] = useState<string[]>([...STANDARD_RU_CITIES]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoadingCities(true);
    fetchRegistrationCities()
      .then((items) => {
        if (!mounted) return;
        const normalized = items.map((city) => city.trim()).filter(Boolean);
        setCities(normalized.length ? normalized : [...STANDARD_RU_CITIES]);
      })
      .catch(() => {
        if (mounted) setCities([...STANDARD_RU_CITIES]);
      })
      .finally(() => {
        if (mounted) setLoadingCities(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const passwordError = useMemo(() => getPasswordError(form.password), [form.password]);
  const passwordConfirmationError = useMemo(
    () => getPasswordConfirmationError(form.password, form.password_confirmation),
    [form.password, form.password_confirmation],
  );

  const setField = (field: keyof FormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError("");

    const requiredMissing =
      !form.company_name.trim() ||
      !form.city.trim() ||
      !form.fullname.trim() ||
      !form.phone.trim() ||
      !form.email.trim() ||
      !form.password;

    if (requiredMissing) {
      setError("Заполните обязательные поля");
      return;
    }
    if (form.phone.replace(/\D/g, "").length !== 11) {
      setError("Введите корректный номер телефона");
      return;
    }
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (passwordConfirmationError) {
      setError(passwordConfirmationError);
      return;
    }

    setSubmitting(true);
    try {
      const email = form.email.trim();
      const password = form.password;

      await registerClient({
        company_name: form.company_name.trim(),
        inn: form.inn.trim() || null,
        city: form.city.trim(),
        fullname: form.fullname.trim(),
        email,
        phone: form.phone.trim(),
        password,
        password_confirm: form.password_confirmation,
      });
      await login(email, password);
      navigate("/");
    } catch (err) {
      const status = (err as any)?.response?.status;
      setError(
        normalizeApiError(
          err,
          status === 409
            ? "Пользователь с таким адресом электронной почты уже зарегистрирован"
            : "Не удалось зарегистрировать пользователя",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-6 py-10">
        <div className="mb-8 flex items-center justify-between text-sm text-slate-500">
          <Link
            to="/auth/login"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm transition hover:bg-slate-50"
          >
            <ArrowLeft size={18} />
            Ко входу
          </Link>
          <span className="text-xs uppercase tracking-[0.35em] text-slate-400">TravelStat</span>
        </div>

        <main className="grid flex-1 items-start gap-8 lg:grid-cols-[0.82fr_1.18fr]">
          <section className="space-y-6 pt-4">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-cyan-100 text-indigo-600 shadow-inner">
              <Building2 size={28} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Регистрация клиента</h1>
              <p className="mt-3 text-sm leading-6 text-slate-500">
                Создайте компанию и пользователя. После регистрации вы сразу попадёте в личный кабинет.
              </p>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
            {error && (
              <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-7">
                  <FormBlock icon={<Building2 size={20} />} title="Компания">
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-medium text-slate-700">Название компании *</span>
                        <input
                          className={inputClass}
                          value={form.company_name}
                          onChange={(event) => setField("company_name", event.target.value)}
                          required
                          autoComplete="organization"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-slate-700">ИНН</span>
                        <input
                          className={inputClass}
                          value={form.inn}
                          onChange={(event) => setField("inn", event.target.value.replace(/\D/g, ""))}
                          inputMode="numeric"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-slate-700">Город *</span>
                        <select
                          className={inputClass}
                          value={form.city}
                          onChange={(event) => setField("city", event.target.value)}
                          required
                        >
                          <option value="">{loadingCities ? "Загрузка городов..." : "Выберите город"}</option>
                          {cities.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </FormBlock>

                  <FormBlock icon={<UserRound size={20} />} title="Пользователь">
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-medium text-slate-700">ФИО *</span>
                        <input
                          className={inputClass}
                          value={form.fullname}
                          onChange={(event) => setField("fullname", event.target.value)}
                          required
                          autoComplete="name"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-slate-700">E-mail *</span>
                        <input
                          className={inputClass}
                          type="email"
                          value={form.email}
                          onChange={(event) => setField("email", event.target.value)}
                          required
                          autoComplete="email"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-slate-700">Телефон *</span>
                        <input
                          className={inputClass}
                          value={form.phone}
                          onChange={(event) => setField("phone", formatRuPhone(event.target.value))}
                          placeholder="+7 (___) ___-__-__"
                          inputMode="tel"
                          autoComplete="tel"
                          required
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-slate-700">Пароль *</span>
                        <input
                          className={inputClass}
                          type="password"
                          value={form.password}
                          onChange={(event) => setField("password", event.target.value)}
                          required
                          autoComplete="new-password"
                        />
                        {form.password && passwordError && (
                          <span className="block text-xs text-amber-700">{passwordError}</span>
                        )}
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-slate-700">Подтверждение пароля *</span>
                        <input
                          className={inputClass}
                          type="password"
                          value={form.password_confirmation}
                          onChange={(event) => setField("password_confirmation", event.target.value)}
                          required
                          autoComplete="new-password"
                        />
                        {form.password_confirmation && passwordConfirmationError && (
                          <span className="block text-xs text-amber-700">
                            {passwordConfirmationError}
                          </span>
                        )}
                      </label>
                    </div>
                  </FormBlock>

                  <div className="flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
                    <Link to="/auth/forgot-password" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                      Забыли пароль?
                    </Link>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(79,70,229,0.25)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50"
                    >
                      <MailCheck size={18} />
                      {submitting ? "Регистрируем..." : "Зарегистрироваться"}
                    </button>
                  </div>
            </form>
          </section>
        </main>
      </div>
    </div>
  );
}

function FormBlock({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="space-y-4">
      <legend className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-900">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-indigo-600">
          {icon}
        </span>
        {title}
      </legend>
      {children}
    </fieldset>
  );
}
